/**
 * Admin /dashboard/projects — list + editor for the Projects blog.
 * Single-broker CRUD: create, edit, publish/unpublish, delete.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Save, Trash2, ExternalLink, Rocket, Wrench, ChevronLeft, Eye, EyeOff, BarChart3 } from "lucide-react";
import Sidebar from "@/components/dashboard/Sidebar";
import CoverImageUpload from "@/components/marketplace/CoverImageUpload";
import {
  fetchAdminProjects, createProject, updateProject, deleteProject, fetchProjectStats, api,
} from "@/lib/api";

const EMPTY = {
  title: "",
  status: "ongoing",
  summary: "",
  body_md: "",
  cover_url: "",
  cover_focal: null,
  tech_tags: [],
  demo_url: "",
  github_url: "",
  linked_domains: [],
  published: false,
  slug: "",
};

export default function AdminProjects() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);   // null = list view; object = editor
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    fetchAdminProjects()
      .then((r) => setItems(r.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (selected) {
    return (
      <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="admin-projects-page">
        <Sidebar />
        <main className="flex-1 p-8 max-w-[1400px]">
          <header className="mb-8">
            <div className="overline" style={{ color: "var(--cd-blue-500)" }}>Portfolio · Projects</div>
            <h1 className="font-display text-4xl tracking-tight mt-2">
              {selected.id ? selected.title || "(untitled)" : "New project"}
            </h1>
          </header>
          <button
            type="button"
            onClick={() => setSelected(null)}
            data-testid="projects-back-list"
            className="cd-btn cd-btn--outline cd-btn--sm"
            style={{ marginBottom: 20 }}
          >
            <ChevronLeft size={14} /> Back to list
          </button>
          <ProjectEditor
            initial={selected}
            onSaved={(saved) => {
              load();
              setSelected(saved);
              toast.success(`Saved · ${saved.published ? "published" : "draft"}`);
            }}
            onDeleted={() => {
              load();
              setSelected(null);
              toast.warning("Project deleted");
            }}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#FAFAFA]" data-testid="admin-projects-page">
      <Sidebar />
      <main className="flex-1 p-8 max-w-[1400px]">
        <header className="mb-8">
          <div className="overline" style={{ color: "var(--cd-blue-500)" }}>Portfolio · Projects</div>
          <h1 className="font-display text-4xl tracking-tight mt-2" data-testid="admin-projects-title">
            Projects · showcase
          </h1>
        </header>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <p style={{ color: "var(--cd-gray-600)", margin: 0, fontSize: 14, maxWidth: 640 }}>
            The blog-style write-ups shown on <Link to="/projects" style={{ color: "var(--cd-blue-500)", fontWeight: 600 }}>/projects</Link>.
            Drafts stay private; publish to push live.
          </p>
          <button
            type="button"
            className="cd-btn cd-btn--primary cd-btn--sm"
            onClick={() => setSelected({ ...EMPTY })}
            data-testid="projects-new"
          >
            <Plus size={14} /> New project
          </button>
        </div>

        {loading ? (
          <p style={{ color: "var(--cd-gray-500)" }}>Loading…</p>
        ) : items.length === 0 ? (
          <div
            data-testid="admin-projects-empty"
            style={{
              padding: "80px 30px", textAlign: "center",
              border: "1px dashed var(--cd-gray-200)", borderRadius: 20,
              color: "var(--cd-gray-500)",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--cd-navy-950)" }}>
              No projects yet.
            </div>
            <div style={{ marginTop: 6, fontSize: 14 }}>
              Click <strong>New project</strong> to write your first entry.
            </div>
          </div>
        ) : (
          <table className="cd-table" data-testid="admin-projects-table" style={{ width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 2px rgba(0,0,0,.04)" }}>
            <thead>
              <tr style={{ textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", fontWeight: 700, background: "#FAFAFA" }}>
                <th style={th}>Title</th>
                <th style={th}>Status</th>
                <th style={th}>Published</th>
                <th style={th}>Views</th>
                <th style={th}>Updated</th>
                <th style={{ ...th, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`admin-project-row-${p.slug}`}
                  style={{ borderTop: "1px solid var(--cd-gray-200)", cursor: "pointer" }}
                  onClick={() => setSelected(p)}
                >
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: "var(--cd-navy-950)" }}>{p.title || "(untitled)"}</div>
                    <div style={{ fontSize: 12, color: "var(--cd-gray-500)", marginTop: 3, fontFamily: "ui-monospace, monospace" }}>/{p.slug}</div>
                  </td>
                  <td style={td}>
                    <span
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                        background: p.status === "finished" ? "#D1FAE5" : "#FEF3C7",
                        color: p.status === "finished" ? "#065F46" : "#92400E",
                      }}
                    >
                      {p.status === "finished" ? <Rocket size={10} strokeWidth={2.4} /> : <Wrench size={10} strokeWidth={2.4} />}
                      {p.status === "finished" ? "Shipped" : "In flight"}
                    </span>
                  </td>
                  <td style={td}>
                    {p.published ? (
                      <span style={{ color: "var(--cd-success, #16a34a)", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Eye size={13} /> Live
                      </span>
                    ) : (
                      <span style={{ color: "var(--cd-gray-500)", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <EyeOff size={13} /> Draft
                      </span>
                    )}
                  </td>
                  <td style={td} data-testid={`admin-project-views-${p.slug}`}>
                    <div style={{ fontWeight: 700, color: "var(--cd-navy-950)", fontSize: 14 }}>
                      {(p.views_total ?? 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--cd-gray-500)", marginTop: 2 }}>
                      {p.views_7d ?? 0} in 7d · {p.unique_visitors_7d ?? 0} unique
                    </div>
                  </td>
                  <td style={{ ...td, fontSize: 13, color: "var(--cd-gray-600)" }}>
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>
                    {p.published && (
                      <a
                        href={`/projects/${p.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "var(--cd-blue-500)", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}
                        data-testid={`admin-project-view-${p.slug}`}
                      >
                        View <ExternalLink size={11} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </main>
    </div>
  );
}

function ProjectEditor({ initial, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...initial,
    tech_tags: initial.tech_tags || [],
    linked_domains: initial.linked_domains || [],
    cover_focal: initial.cover_focal || null,
  });
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;

  const patch = (partial) => setForm((f) => ({ ...f, ...partial }));

  const save = async (publish = null) => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        status: form.status,
        summary: form.summary,
        body_md: form.body_md,
        cover_url: form.cover_url || null,
        cover_focal: form.cover_focal || null,
        tech_tags: (form.tech_tags || []).filter(Boolean),
        demo_url: form.demo_url || null,
        github_url: form.github_url || null,
        linked_domains: (form.linked_domains || []).filter(Boolean),
        published: publish ?? form.published,
        slug: form.slug || null,
      };
      const saved = isNew
        ? await createProject(payload)
        : await updateProject(initial.id, payload);
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${form.title}"? This cannot be undone.`)) return;
    try {
      await deleteProject(initial.id);
      onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const tagsStr = (form.tech_tags || []).join(", ");

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 30 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <Field label="Title" required>
          <input
            className="cd-input"
            value={form.title}
            onChange={(e) => patch({ title: e.target.value })}
            data-testid="editor-title"
            placeholder="e.g. Automating .nl drop lists with Realtime Register"
          />
        </Field>

        <Field label="Summary" hint="One-line pitch shown on the /projects grid.">
          <textarea
            className="cd-input"
            rows={2}
            value={form.summary}
            onChange={(e) => patch({ summary: e.target.value })}
            data-testid="editor-summary"
            placeholder="Short elevator-pitch — 1-2 sentences."
          />
        </Field>

        <Field
          label="Body (markdown)"
          hint="Supports `## Section title`, blank-line paragraphs, `- bullets`, **bold**, _italic_, `code`, [links](https://)."
        >
          <textarea
            className="cd-input"
            rows={18}
            value={form.body_md}
            onChange={(e) => patch({ body_md: e.target.value })}
            data-testid="editor-body"
            placeholder={`Optional lede paragraph up top.\n\n## Why I built it\nSome context…\n\n## How it works\n- Bullet one\n- Bullet two`}
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 14, lineHeight: 1.6 }}
          />
        </Field>
      </div>

      <aside style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {!isNew && <ProjectStatsCard slug={initial.slug} />}
        <div style={{ padding: 20, border: "1px solid var(--cd-gray-200)", borderRadius: 14, background: "var(--cd-white)" }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginBottom: 14 }}>
            Publish
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              type="button"
              className="cd-btn cd-btn--primary"
              onClick={() => save(true)}
              disabled={saving}
              data-testid="editor-publish"
            >
              <Save size={14} /> {form.published ? "Save (published)" : "Publish"}
            </button>
            <button
              type="button"
              className="cd-btn cd-btn--outline cd-btn--sm"
              onClick={() => save(false)}
              disabled={saving}
              data-testid="editor-save-draft"
            >
              Save as draft
            </button>
            {!isNew && (
              <button
                type="button"
                onClick={remove}
                className="cd-btn cd-btn--outline cd-btn--sm"
                style={{ color: "#BE123C", borderColor: "#FCA5A5" }}
                data-testid="editor-delete"
              >
                <Trash2 size={12} /> Delete
              </button>
            )}
          </div>
        </div>

        <Field label="Status">
          <select
            className="cd-input"
            value={form.status}
            onChange={(e) => patch({ status: e.target.value })}
            data-testid="editor-status"
          >
            <option value="ongoing">In flight</option>
            <option value="finished">Shipped</option>
          </select>
        </Field>

        <Field label="Slug" hint="URL suffix. Leave blank to auto-generate from title.">
          <input
            className="cd-input"
            value={form.slug || ""}
            onChange={(e) => patch({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
            data-testid="editor-slug"
            placeholder="auto"
          />
        </Field>

        <Field label="Cover image" hint="Drag-and-drop, click to browse, or click on the preview to set a focal point.">
          <CoverImageUpload
            value={form.cover_url}
            onChange={(url) => patch({ cover_url: url })}
            focal={form.cover_focal}
            onFocalChange={(f) => patch({ cover_focal: f })}
          />
        </Field>

        <Field label="Linked domains" hint="Comma-separated listing slugs (e.g. `prim.io, usdmarket.net`). Each lander back-links to this write-up.">
          <input
            className="cd-input"
            value={(form.linked_domains || []).join(", ")}
            onChange={(e) => patch({ linked_domains: e.target.value.split(",").map((s) => s.trim().toLowerCase()) })}
            data-testid="editor-linked-domains"
            placeholder="prim.io, usdmarket.net"
          />
        </Field>

        <Field label="Live demo URL">
          <input
            className="cd-input"
            value={form.demo_url || ""}
            onChange={(e) => patch({ demo_url: e.target.value })}
            data-testid="editor-demo"
            placeholder="https://…"
          />
        </Field>

        <Field label="GitHub / source URL">
          <input
            className="cd-input"
            value={form.github_url || ""}
            onChange={(e) => patch({ github_url: e.target.value })}
            data-testid="editor-github"
            placeholder="https://github.com/…"
          />
        </Field>

        <Field label="Tech tags" hint="Comma-separated. Shown as chips on the grid.">
          <input
            className="cd-input"
            value={tagsStr}
            onChange={(e) => patch({ tech_tags: e.target.value.split(",").map((s) => s.trim()) })}
            data-testid="editor-tags"
            placeholder="Next.js, PostgreSQL, Stripe"
          />
        </Field>
      </aside>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--cd-navy-950)", letterSpacing: ".02em" }}>
          {label}{required && <span style={{ color: "#BE123C" }}> *</span>}
        </span>
        {hint && <span style={{ fontSize: 11, color: "var(--cd-gray-500)" }}>{hint}</span>}
      </div>
      {children}
    </label>
  );
}

const th = { padding: "12px 14px", fontSize: 11 };
const td = { padding: "16px 14px", fontSize: 14, verticalAlign: "middle" };


function ProjectStatsCard({ slug }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) { setStats(null); setLoading(false); return; }
    setLoading(true);
    fetchProjectStats(slug)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div
      style={{ padding: 20, border: "1px solid var(--cd-gray-200)", borderRadius: 14, background: "var(--cd-white)" }}
      data-testid={`project-stats-${slug}`}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginBottom: 14 }}>
        <BarChart3 size={12} strokeWidth={2.2} /> Traffic
      </div>
      {loading ? (
        <div style={{ fontSize: 13, color: "var(--cd-gray-500)" }}>Loading…</div>
      ) : !stats ? (
        <div style={{ fontSize: 13, color: "var(--cd-gray-500)" }}>No data yet.</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
            <StatCell label="Total views" value={stats.views_total} testid={`stats-total-${slug}`} />
            <StatCell label="7 days" value={stats.views_7d} testid={`stats-7d-${slug}`} />
            <StatCell label="30 days" value={stats.views_30d} testid={`stats-30d-${slug}`} />
          </div>
          {stats.daily_views && stats.daily_views.length > 0 && (
            <div style={{ marginBottom: 14 }} data-testid={`sparkline-${slug}`}>
              <Sparkline data={stats.daily_views} slug={slug} />
            </div>
          )}
          <div style={{ fontSize: 12, color: "var(--cd-gray-600)", marginBottom: 12 }}>
            <strong style={{ color: "var(--cd-navy-950)" }}>{(stats.unique_visitors_total || 0).toLocaleString()}</strong> unique visitors
          </div>
          {stats.top_referrers && stats.top_referrers.length > 0 && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginBottom: 8 }}>
                Top referrers
              </div>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {stats.top_referrers.map((r, i) => (
                  <li
                    key={i}
                    style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12 }}
                    data-testid={`referrer-${slug}-${i}`}
                  >
                    <span
                      style={{ color: "var(--cd-gray-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }}
                      title={r.referrer}
                    >
                      {shortReferrer(r.referrer)}
                    </span>
                    <span style={{ color: "var(--cd-navy-950)", fontWeight: 600 }}>{r.views}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StatCell({ label, value, testid }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--cd-navy-950)", lineHeight: 1 }} data-testid={testid}>
        {(value ?? 0).toLocaleString()}
      </div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

function shortReferrer(url) {
  try {
    const u = new URL(url);
    return u.hostname + (u.pathname && u.pathname !== "/" ? u.pathname : "");
  } catch {
    return url;
  }
}

/**
 * Minimal 30-day daily-views sparkline. Renders an SVG path with a soft
 * gradient fill and a dot on the peak day so viral spikes are visually
 * obvious. Hovering shows a floating tooltip with the exact date, view
 * count, and top referrers for that day. Deliberately dependency-free —
 * no recharts, no ResizeObserver, just a fixed 200×48 viewBox scaled by
 * CSS width:100%.
 */
function Sparkline({ data, slug }) {
  const [hover, setHover] = useState(null); // { idx, x, y }
  const [drillDate, setDrillDate] = useState(null);
  if (!data || data.length === 0) return null;
  const W = 200;
  const H = 48;
  const pad = 3;
  const maxV = Math.max(1, ...data.map((d) => d.views));
  const n = data.length;
  const stepX = (W - pad * 2) / Math.max(1, n - 1);

  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = H - pad - (d.views / maxV) * (H - pad * 2);
    return { x, y, v: d.views, date: d.date, refs: d.top_referrers || [] };
  });

  const peakIdx = points.reduce((best, p, i) => (p.v > points[best].v ? i : best), 0);
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${points[n - 1].x.toFixed(1)},${H - pad} L${points[0].x.toFixed(1)},${H - pad} Z`;
  const total = data.reduce((s, d) => s + d.views, 0);
  const gradId = `sparkfill-${Math.random().toString(36).slice(2, 8)}`;

  const onMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    // Find nearest point by x
    let bestIdx = 0;
    let bestDist = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - relX);
      if (d < bestDist) { bestDist = d; bestIdx = i; }
    });
    setHover({ idx: bestIdx, containerRect: rect });
  };

  const active = hover ? points[hover.idx] : null;

  return (
    <div style={{ position: "relative" }} onMouseLeave={() => setHover(null)}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--cd-gray-500)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 6 }}>
        <span>Last 30 days</span>
        <span>{total.toLocaleString()} views · peak {points[peakIdx].v} on {formatShortDate(points[peakIdx].date)}</span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: 48, display: "block", cursor: slug ? "pointer" : "default" }}
        role="img"
        aria-label={`30-day views sparkline. ${total} total views, peak ${points[peakIdx].v} on ${points[peakIdx].date}`}
        onMouseMove={onMove}
        onClick={() => { if (slug && active && active.v > 0) setDrillDate(active.date); }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--cd-blue-500, #2563eb)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--cd-blue-500, #2563eb)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke="var(--cd-blue-500, #2563eb)" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
        {points[peakIdx].v > 0 && (
          <circle
            cx={points[peakIdx].x}
            cy={points[peakIdx].y}
            r="2.6"
            fill="var(--cd-blue-500, #2563eb)"
            stroke="white"
            strokeWidth="1.4"
          />
        )}
        {active && (
          <>
            <line
              x1={active.x}
              x2={active.x}
              y1={pad}
              y2={H - pad}
              stroke="var(--cd-blue-500, #2563eb)"
              strokeWidth="0.6"
              strokeDasharray="1.5,1.5"
              opacity="0.6"
            />
            <circle cx={active.x} cy={active.y} r="2.4" fill="white" stroke="var(--cd-blue-500, #2563eb)" strokeWidth="1.4" />
          </>
        )}
      </svg>
      {active && (
        <SparkTooltip point={active} containerRect={hover.containerRect} clickable={!!slug} />
      )}
      {drillDate && slug && (
        <DayDrilldown slug={slug} date={drillDate} onClose={() => setDrillDate(null)} />
      )}
    </div>
  );
}

function SparkTooltip({ point, containerRect, clickable }) {
  // Anchor tooltip to the SVG X position, but clamp within the card so
  // the far-right days don't overflow.
  const svgW = containerRect?.width || 200;
  const percent = point.x / 200;
  const left = Math.max(0, Math.min(percent * svgW - 90, svgW - 180));
  return (
    <div
      style={{
        position: "absolute",
        left,
        bottom: 68,
        width: 180,
        padding: "10px 12px",
        background: "var(--cd-navy-950, #03142f)",
        color: "white",
        borderRadius: 8,
        fontSize: 12,
        lineHeight: 1.35,
        boxShadow: "0 8px 24px rgba(3,20,47,.25)",
        pointerEvents: "none",
        zIndex: 20,
      }}
      data-testid="sparkline-tooltip"
    >
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", opacity: 0.72, fontWeight: 700 }}>
        {formatShortDate(point.date)}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
        {point.v} {point.v === 1 ? "view" : "views"}
      </div>
      {point.refs.length > 0 ? (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,.14)" }}>
          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", opacity: 0.55, fontWeight: 700, marginBottom: 4 }}>
            Top referrers
          </div>
          {point.refs.map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 6, fontSize: 11 }}>
              <span style={{ opacity: 0.85, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {shortReferrer(r.referrer)}
              </span>
              <span style={{ fontWeight: 600 }}>{r.views}</span>
            </div>
          ))}
        </div>
      ) : point.v > 0 ? (
        <div style={{ marginTop: 6, fontSize: 11, opacity: 0.65 }}>
          Direct / unknown referrer
        </div>
      ) : null}
      {clickable && point.v > 0 && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,.14)", fontSize: 10, opacity: 0.7 }}>
          Click for full breakdown →
        </div>
      )}
    </div>
  );
}

function DayDrilldown({ slug, date, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${slug}/stats/day`, { params: { date } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug, date]);

  const hourlyMax = data ? Math.max(1, ...data.hourly) : 1;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(3,20,47,0.55)",
        backdropFilter: "blur(4px)", zIndex: 50,
        display: "flex", justifyContent: "flex-end",
      }}
      onClick={onClose}
      data-testid="sparkline-drilldown"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 480, maxWidth: "100vw", background: "white",
          height: "100vh", overflow: "auto", padding: 28,
          boxShadow: "-24px 0 48px rgba(3,20,47,.28)",
          fontFamily: "var(--cd-font)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div className="overline">Traffic · daily breakdown</div>
            <h2 style={{ fontFamily: "var(--cd-font-display)", fontSize: 22, marginTop: 4 }}>
              {formatLongDate(date)}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{ padding: 6, borderRadius: 6, border: "1px solid var(--cd-gray-200)" }}
            aria-label="Close"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
        </div>

        {loading ? (
          <div style={{ fontSize: 13, color: "var(--cd-gray-500)" }}>Loading…</div>
        ) : !data ? (
          <div style={{ fontSize: 13, color: "var(--cd-gray-500)" }}>Couldn't load stats for this day.</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              <div style={{ padding: 16, border: "1px solid var(--cd-gray-200)", borderRadius: 12 }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: "var(--cd-navy-950)" }} data-testid="drill-total">
                  {data.views.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginTop: 4 }}>
                  Total views
                </div>
              </div>
              <div style={{ padding: 16, border: "1px solid var(--cd-gray-200)", borderRadius: 12 }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: "var(--cd-navy-950)" }}>
                  {data.unique_visitors.toLocaleString()}
                </div>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginTop: 4 }}>
                  Unique visitors
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginBottom: 8 }}>
                Hourly · when the spike hit
              </div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80 }} data-testid="drill-hourly">
                {data.hourly.map((v, h) => (
                  <div
                    key={h}
                    title={`${String(h).padStart(2, "0")}:00 · ${v} view${v === 1 ? "" : "s"}`}
                    style={{
                      flex: 1,
                      background: v === 0 ? "var(--cd-gray-100)" : "var(--cd-blue-500, #2563eb)",
                      height: `${Math.max(4, (v / hourlyMax) * 100)}%`,
                      borderRadius: 2,
                      opacity: v === 0 ? 0.4 : 1,
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "var(--cd-gray-500)", marginTop: 4, fontFamily: "ui-monospace, monospace" }}>
                <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
              </div>
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)", marginBottom: 8 }}>
                Referrers ({data.referrers.length})
              </div>
              {data.referrers.length === 0 ? (
                <div style={{ fontSize: 13, color: "var(--cd-gray-500)" }}>No referrer data.</div>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.referrers.map((r, i) => (
                    <li
                      key={i}
                      style={{
                        display: "flex", justifyContent: "space-between",
                        padding: "10px 12px", border: "1px solid var(--cd-gray-200)",
                        borderRadius: 8, fontSize: 13,
                      }}
                      data-testid={`drill-referrer-${i}`}
                    >
                      <span style={{ color: "var(--cd-gray-700)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }} title={r.referrer}>
                        {r.referrer === "(direct)" ? <em style={{ opacity: 0.7 }}>Direct / unknown</em> : shortReferrer(r.referrer)}
                      </span>
                      <span style={{ color: "var(--cd-navy-950)", fontWeight: 700 }}>{r.views}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
}

function formatLongDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function formatShortDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

